import { useEffect, useRef, useState } from 'react';
import validator from 'validator';
import { AddAPhoto as AddAPhotoIcon } from '@mui/icons-material';
import {
  Button,
  Input,
  InputPrice,
  InputNumber,
  ImageGallery,
  ImageGalleryCard,
  ImageGalleryCardWrapper,
  Typography,
  enqueueSnackbar,
  fullScreenButtonClick,
  Select,
} from '../../design-system';
import {
  DashboardFormPopup,
  DashboardHeader,
  CloseIconButton,
  HideInput,
  DashbaordFormFooter,
  DashboardInputs,
} from '../dashboard';
import {
  useCallBackOnBack,
  useCompleteRepairMutation,
  useConvertCurrencyFcQuery,
  useRepairDeleteMutation,
  useUpdateImagesRepairMutation,
  useUpdateRepairMutation,
} from '../../hooks';
import { filesFormatValidator } from '../../utils';
import {
  CURRENCIES,
  currencyOptions,
  INPUT_ERRORS,
  MESSAGES,
  repairStatusLabel,
} from '../../const';
import { CurrencyCode, RepairGetAllQuery, RepairStatus } from '../../types';

interface RepairsViewProps {
  repair: RepairGetAllQuery['data'][number] | Record<string, never>;
  isOpen: boolean;
  refetch: () => void;
  handleFormCloseClick: () => void;
}

const isReadOnlyStatus = (status?: RepairStatus) => status === 'REJECTED' || status === 'COMPLETED';

const canUpdateStatus = (status?: RepairStatus) => status === 'PENDING' || status === 'ACTIVE';

const canCompleteStatus = (status?: RepairStatus) => status === 'ACTIVE';

const canDeleteStatus = (status?: RepairStatus) =>
  status === 'DRAFT' || status === 'PENDING' || status === 'ACTIVE';

const canEditImagesStatus = (status?: RepairStatus) => !isReadOnlyStatus(status);

export const RepairsView: React.FC<RepairsViewProps> = ({
  isOpen,
  refetch,
  repair,
  handleFormCloseClick,
}) => {
  const updateRepairMutation = useUpdateRepairMutation();
  const completeRepairMutation = useCompleteRepairMutation();
  const deleteRepairMutation = useRepairDeleteMutation();
  const updateImagesMutation = useUpdateImagesRepairMutation();
  const convertCurrency = useConvertCurrencyFcQuery();

  const [price, setPrice] = useState('');
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('USD');
  const [expectedDurationDays, setExpectedDurationDays] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isCompleteSubmitClicked, setIsCompleteSubmitClicked] = useState(false);

  const [oldBeforeImages, setOldBeforeImages] = useState<any[]>([]);
  const [beforeImages, setBeforeImages] = useState<any[]>([]);
  const [beforeImagesFiles, setBeforeImagesFiles] = useState<any[]>([]);
  const deleteBeforePhotos = useRef<any[]>([]);
  const beforeImagesInputRef = useRef<any>();

  const [oldAfterImages, setOldAfterImages] = useState<any[]>([]);
  const [afterImages, setAfterImages] = useState<any[]>([]);
  const [afterImagesFiles, setAfterImagesFiles] = useState<any[]>([]);
  const deleteAfterPhotos = useRef<any[]>([]);
  const afterImagesInputRef = useRef<any>();

  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [formKey, setFormKey] = useState(0);

  const status = repair.status as RepairStatus | undefined;
  const isReadOnly = isReadOnlyStatus(status);
  const canUpdate = canUpdateStatus(status);
  const canComplete = canCompleteStatus(status);
  const canDelete = canDeleteStatus(status);
  const canEditImages = canEditImagesStatus(status);
  const canSave = canUpdate || status === 'DRAFT';

  const resetForm = (data: RepairsViewProps['repair']) => {
    setPrice(data.price ? String(data.price) : '0');
    setCurrencyCode(data.currencyCode || 'USD');
    setExpectedDurationDays(data.expectedDurationDays ? String(data.expectedDurationDays) : '0');
    setDescription(data.description || '');
    setIsSubmitClicked(false);
    setIsCompleteSubmitClicked(false);

    deleteBeforePhotos.current = [];
    deleteAfterPhotos.current = [];
    setOldBeforeImages(data.beforeImages || []);
    setBeforeImages([]);
    setBeforeImagesFiles([]);
    setOldAfterImages(data.afterImages || []);
    setAfterImages([]);
    setAfterImagesFiles([]);

    if (beforeImagesInputRef.current) {
      beforeImagesInputRef.current.type = 'text';
      beforeImagesInputRef.current.type = 'file';
    }
    if (afterImagesInputRef.current) {
      afterImagesInputRef.current.type = 'text';
      afterImagesInputRef.current.type = 'file';
    }

    setFormKey((key) => key + 1);
  };

  useEffect(() => {
    if (!isOpen) return;

    resetForm(repair);
  }, [isOpen, repair?.id]);

  useCallBackOnBack(handleFormCloseClick, isOpen);

  const handleCurrencyChange = (newCurrencyCode: CurrencyCode) => {
    if (newCurrencyCode === currencyCode) return;

    const convertedValue = convertCurrency(currencyCode, newCurrencyCode, Number(price) || 0);

    setPrice(convertedValue.toFixed(2));
    setCurrencyCode(newCurrencyCode);
    setFormKey((key) => key + 1);
  };

  const validators = {
    price: !String(price).length
      ? INPUT_ERRORS.REQUIRED.PRICE
      : Number(price) <= 0
      ? INPUT_ERRORS.NOT_VALID.PRICE_MIN_VALUE
      : '',
    expectedDurationDays: !String(expectedDurationDays).length
      ? INPUT_ERRORS.REQUIRED.DAYS_TO_COMPLETE
      : Number(expectedDurationDays) <= 0
      ? INPUT_ERRORS.NOT_VALID.DAYS_TO_COMPLETE_MIN_VALUE
      : '',
    description: !description
      ? INPUT_ERRORS.REQUIRED.DESCRIPTION
      : !validator.isLength(description, { min: 10, max: 500 })
      ? INPUT_ERRORS.NOT_VALID.REPAIR_DESCRIPTION_LENGTH
      : '',
    beforeImages: ![...oldBeforeImages, ...beforeImages].length ? INPUT_ERRORS.REQUIRED.IMAGES : '',
  };

  const handleZoomImageClick = (images: any[], i: number) => {
    setGalleryImages(images.map((img) => img.url || img));
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleChangeImages = (
    newImages: any[],
    newFiles: any[],
    setImages: React.Dispatch<React.SetStateAction<any[]>>,
    setFiles: React.Dispatch<React.SetStateAction<any[]>>,
    inputRef: React.MutableRefObject<any>,
  ) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    setImages((prev) => [...prev, ...newImages]);
    setFiles((prev) => [...prev, ...newFiles]);
    inputRef.current.type = 'text';
    inputRef.current.type = 'file';
  };

  const handleDeleteImageClick = (
    i: number,
    oldImages: any[],
    setOldImages: React.Dispatch<React.SetStateAction<any[]>>,
    newImages: any[],
    setNewImages: React.Dispatch<React.SetStateAction<any[]>>,
    newFiles: any[],
    setNewFiles: React.Dispatch<React.SetStateAction<any[]>>,
    deletePhotos: React.MutableRefObject<any[]>,
  ) => {
    if (i - oldImages.length >= 0) {
      const index = i - oldImages.length;

      setNewImages(newImages.filter((_, imageIndex) => imageIndex !== index));
      setNewFiles(newFiles.filter((_, fileIndex) => fileIndex !== index));
      return;
    }

    deletePhotos.current = [...deletePhotos.current, { action: 'delete', name: oldImages[i].name }];
    setOldImages(oldImages.filter((_, idx) => idx !== i));
  };

  const uploadImagesIfChanged = async (action: 'before' | 'after', photos: any[]) => {
    if (!photos.length) return;

    await updateImagesMutation.mutateAsync({
      id: repair.id,
      photos,
      action,
    });
  };

  const getChangedPhotos = (files: any[], deletedPhotos: any[]) => {
    const uploadPhotos = files.map((file) => ({
      action: 'upload' as const,
      file,
    }));

    return [...uploadPhotos, ...deletedPhotos];
  };

  const fieldValidators = {
    price: validators.price,
    expectedDurationDays: validators.expectedDurationDays,
    description: validators.description,
  };

  const hasFormFieldsChanged = () => {
    const originalPrice = Number(repair.price || 0).toFixed(2);
    const currentPrice = Number(price || 0).toFixed(2);

    return (
      currencyCode !== (repair.currencyCode || 'USD') ||
      currentPrice !== originalPrice ||
      Number(expectedDurationDays) !== Number(repair.expectedDurationDays || 0) ||
      description !== (repair.description || '')
    );
  };

  const saveRepairFields = async () => {
    await updateRepairMutation.mutateAsync({
      id: repair.id,
      price: Number(price),
      currencyCode,
      expectedDurationDays: Number(expectedDurationDays),
      description,
    });
  };

  const handleSaveClick = async () => {
    setIsSubmitClicked(true);

    if (beforeImages.length > 10 || afterImages.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (canUpdate && Object.values(validators).some((error) => error)) {
      return;
    }

    try {
      if (canUpdate) {
        await saveRepairFields();
      }

      await uploadImagesIfChanged(
        'before',
        getChangedPhotos(beforeImagesFiles, deleteBeforePhotos.current),
      );
      await uploadImagesIfChanged(
        'after',
        getChangedPhotos(afterImagesFiles, deleteAfterPhotos.current),
      );

      refetch();
      handleFormCloseClick();
      enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.UPDATED, 'success');
    } catch {
      enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_UPDATED, 'error');
    }
  };

  const hasAfterImages = [...oldAfterImages, ...afterImages].length > 0;
  const showAfterImagesError = isCompleteSubmitClicked && canComplete && !hasAfterImages;

  const handleCompleteClick = async () => {
    setIsCompleteSubmitClicked(true);

    if (!hasAfterImages) {
      enqueueSnackbar(INPUT_ERRORS.REQUIRED.IMAGES, 'warning');
      return;
    }

    if (beforeImages.length > 10 || afterImages.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (hasFormFieldsChanged() && canUpdate) {
      setIsSubmitClicked(true);

      if (Object.values(fieldValidators).some((error) => error)) {
        return;
      }
    }

    try {
      if (hasFormFieldsChanged() && canUpdate) {
        await saveRepairFields();
      }

      await uploadImagesIfChanged(
        'after',
        getChangedPhotos(afterImagesFiles, deleteAfterPhotos.current),
      );
      await uploadImagesIfChanged(
        'before',
        getChangedPhotos(beforeImagesFiles, deleteBeforePhotos.current),
      );
      await completeRepairMutation.mutateAsync({ id: repair.id });

      refetch();
      handleFormCloseClick();
      enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.COMPLETED, 'success');
    } catch {
      enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_COMPLETED, 'error');
    }
  };

  const handleDeleteClick = async () => {
    const confirmed = await confirm('Are you sure you want to delete this repair?');
    if (!confirmed) return;

    try {
      await deleteRepairMutation.mutateAsync({ id: repair.id });
      refetch();
      handleFormCloseClick();
      enqueueSnackbar(MESSAGES.REPAIR.SUCCESS.DELETED, 'success');
    } catch {
      enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_DELETED, 'error');
    }
  };

  const renderImagesSection = (
    title: string,
    sourceImages: any[],
    oldImages: any[],
    setOldImages: React.Dispatch<React.SetStateAction<any[]>>,
    newImages: any[],
    setNewImages: React.Dispatch<React.SetStateAction<any[]>>,
    newFiles: any[],
    setNewFiles: React.Dispatch<React.SetStateAction<any[]>>,
    deletePhotos: React.MutableRefObject<any[]>,
    inputRef: React.MutableRefObject<any>,
    hideDeleteOnLastImage = false,
    titleColor?: '--gray-100' | '--red-100',
  ) => {
    const imagesToShow = canEditImages ? [...oldImages, ...newImages] : sourceImages;

    return (
      <DashboardInputs>
        <DashboardInputs columns={canEditImages ? '1fr max-content' : '1fr'}>
          <Typography variant={'textM'} weight={'bold'} color={titleColor || '--gray-100'}>
            {title}
          </Typography>
          {canEditImages && (
            <Button onClick={() => inputRef.current.click()} isSquare>
              <AddAPhotoIcon />
            </Button>
          )}
        </DashboardInputs>
        {canEditImages && (
          <HideInput>
            <Input
              label={title}
              data-size={'medium'}
              type={'file'}
              inputRef={inputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
              }}
              value={undefined}
              handleChangeFiles={(newImgs, newFls) =>
                handleChangeImages(newImgs, newFls, setNewImages, setNewFiles, inputRef)
              }
            />
          </HideInput>
        )}
        <ImageGalleryCardWrapper>
          {imagesToShow.map((img: any, i: number) => (
            <ImageGalleryCard
              key={i}
              img={img.url || img}
              handleZoomClick={() => handleZoomImageClick(imagesToShow, i)}
              handleDeleteImageClick={() =>
                handleDeleteImageClick(
                  i,
                  oldImages,
                  setOldImages,
                  newImages,
                  setNewImages,
                  newFiles,
                  setNewFiles,
                  deletePhotos,
                )
              }
              hideDeleteButton={
                !canEditImages || (hideDeleteOnLastImage && imagesToShow.length <= 1)
              }
            />
          ))}
        </ImageGalleryCardWrapper>
      </DashboardInputs>
    );
  };

  return (
    <DashboardFormPopup isOpen={isOpen}>
      <ImageGallery photos={galleryImages} startIndex={zoomImageIndex} />
      <DashboardHeader>
        <Typography variant={'textXL'} weight={'bold'}>
          Repair: {repair?.container?.number || 'Container not found'}
        </Typography>
        <CloseIconButton onClick={handleFormCloseClick} />
      </DashboardHeader>
      <DashboardInputs>
        <Input
          label={'Status'}
          data-size={'medium'}
          value={repairStatusLabel[status || ''] || status || ''}
          readOnly
        />
        {canUpdate && !isReadOnly ? (
          <Select
            label={'Currency'}
            placeholder={'Select Currency'}
            options={currencyOptions}
            value={currencyCode}
            onSelect={handleCurrencyChange}
          />
        ) : (
          <Input label={'Currency'} data-size={'medium'} value={currencyCode} readOnly />
        )}
        <InputPrice
          key={`price-${formKey}`}
          label={'Price'}
          data-size={'medium'}
          defaultValue={price || '0'}
          onChange={(e) => setPrice(e.target.value)}
          readOnly={isReadOnly || !canUpdate}
          error={isSubmitClicked && canUpdate && !!validators.price}
          helperText={isSubmitClicked && canUpdate ? validators.price : ''}
          endIcon={CURRENCIES[currencyCode].symbol}
        />
        <InputNumber
          key={`days-${formKey}`}
          label={'Days To Complete'}
          placeholder={'Days'}
          data-size={'medium'}
          defaultValue={repair.expectedDurationDays ? String(repair.expectedDurationDays) : '0'}
          onChange={(e) => setExpectedDurationDays(e.target.value)}
          readOnly={isReadOnly || !canUpdate}
          error={isSubmitClicked && canUpdate && !!validators.expectedDurationDays}
          helperText={isSubmitClicked && canUpdate ? validators.expectedDurationDays : ''}
        />
        <Input
          label={'Description'}
          data-size={'medium'}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          multiline
          readOnly={isReadOnly || !canUpdate}
          error={isSubmitClicked && canUpdate && !!validators.description}
          helperText={isSubmitClicked && canUpdate ? validators.description : ''}
        />
        {renderImagesSection(
          'Before Images',
          repair.beforeImages || [],
          oldBeforeImages,
          setOldBeforeImages,
          beforeImages,
          setBeforeImages,
          beforeImagesFiles,
          setBeforeImagesFiles,
          deleteBeforePhotos,
          beforeImagesInputRef,
          true,
        )}
        {renderImagesSection(
          canComplete ? 'After Images*' : 'After Images',
          repair.afterImages || [],
          oldAfterImages,
          setOldAfterImages,
          afterImages,
          setAfterImages,
          afterImagesFiles,
          setAfterImagesFiles,
          deleteAfterPhotos,
          afterImagesInputRef,
          false,
          showAfterImagesError ? '--red-100' : '--gray-100',
        )}
      </DashboardInputs>
      {!isReadOnly && (
        <DashbaordFormFooter>
          <DashboardInputs columns={'1fr 1fr'}>
            {canDelete && (
              <Button size={'medium'} data-color={'destructive'} onClick={handleDeleteClick}>
                Delete
              </Button>
            )}
            <Button size={'medium'} onClick={handleCompleteClick} disabled={!canComplete}>
              Complete
            </Button>
            {canSave && (
              <>
                <Button size={'medium'} data-color={'white'} onClick={() => resetForm(repair)}>
                  Cancel
                </Button>
                <Button size={'medium'} onClick={handleSaveClick}>
                  Save
                </Button>
              </>
            )}
          </DashboardInputs>
        </DashbaordFormFooter>
      )}
    </DashboardFormPopup>
  );
};
